import { acceptCurrentPrice, addToCart, clearCart, readCart, removeFromCart, setQuantity } from "./lib/cart.js";
import {
  ADDRESS, escapeHtml, footer, header, kyivToday, MAP_URL, money, PHONE_DISPLAY, PHONE_TEL,
  productImage, productTile, setPageMeta, showNotice, updateCartCount
} from "./lib/format.js";
import { attachProductSelectionTracking, track, trackItems, trackOrderRequest } from "./lib/tracking.js";
import { resolveTurnstileConfig } from "./lib/turnstile-config.js";
import { homeMarkup, collectionMarkup, productMarkup } from "./lib/storefront-views.js";
import { attachShoppingUI } from "./lib/shopping-ui.js";
import { deliveryCostCents } from "./lib/delivery.js";
import { checkoutMarkup } from "./lib/checkout-view.js";
import { collectionText } from "./lib/collection-model.js";
import { localizedPath } from "./lib/locale.js";
import { catalogMatches, filterCatalog, deliverySummary, readFavorites, toggleFavorite } from "./lib/storefront-model.js";

const INSTAGRAM_URL = "https://www.instagram.com/kvitkova_oaza/";
let checkoutCatalogProposal = null;

function navigatePublic(href) {
  window.location.assign(localizedPath(href, document.documentElement.lang));
}

export async function renderStorefront(path) {
  const app = document.querySelector("#app");
  const serverRoot = document.querySelector("#page-root");
  const { mode: turnstileMode, sitekey: turnstileSitekey } = resolveTurnstileConfig(serverRoot, app);
  app.dataset.turnstileMode = turnstileMode;
  app.dataset.turnstileSitekey = turnstileSitekey;
  const serverRenderedPage = Boolean(document.querySelector("#app main.home-main, #app main.collection-main, #app #page-root[data-page='product']"));
  if (serverRenderedPage) {
    attachStorefrontEvents();
    updateCartCount();
    const tiles = [...document.querySelectorAll(".product-tile[data-product-id]")].map(productFromDataset);
    if (tiles.length) trackItems("view_item_list", tiles);
    const detail = document.querySelector(".product-detail[data-product-id]");
    if (detail) trackItems("view_item", [productFromDataset(detail)]);
    return;
  }
  const collections = await fetch("/api/collections").then((r) => r.ok ? r.json() : { collections: [] }).then((p) => p.collections).catch(() => []);
  app.innerHTML = `${header({ collections })}<main id="main-content" class="page-main"><div class="loading-state" role="status">Завантажуємо…</div></main>${footer({ compact: path.startsWith("/products/"), collections })}<div id="global-live-region" class="notice" role="status" aria-live="polite" hidden></div>`;
  const main = document.querySelector("#main-content");

  if (path === "/") await renderHome(main);
  else if (/^\/collections\/[a-z0-9-]+$/.test(path)) await renderCollection(main, path.split("/").at(-1) === "all" ? null : path.split("/").at(-1));
  else if (path === "/favorites") await renderFavorites(main);
  else if (path.startsWith("/products/")) await renderProduct(main, decodeURIComponent(path.slice("/products/".length)));
  else if (path === "/cart") await renderCart(main);
  else if (path === "/checkout") await renderCheckout(main);
  else if (path === "/order-received") await renderReceipt(main);
  else if (path === "/delivery") renderDelivery(main);
  else if (path === "/contacts") renderContacts(main);
  else renderNotFound(main);

  attachStorefrontEvents();
}

function productFromDataset(element) {
  return {
    id: element.dataset.productId,
    name: element.dataset.productName,
    collection: element.dataset.productCollection,
    number: element.dataset.productNumber,
    priceCents: Number(element.dataset.productPriceCents)
  };
}

async function renderHome(main) {
  let catalog;
  try { catalog = await getProducts("sort=number"); } catch { catalog = null; }
  const params = new URLSearchParams(window.location.search);
  const products = catalog?.products || [];
  const visible = products.filter((p) => !params.get("q") || catalogMatches(p, params.get("q")));
  setPageMeta("Букети у Львові | Квіткова Оаза", "Осінні та весільні букети з цінами. Безкоштовна доставка від 3500 грн.");
  main.className = "page-main home-main";
  main.innerHTML = homeMarkup(filterCatalog(visible, params), products, { failed: !catalog, params, collections: catalog?.collections || [], language: document.documentElement.lang });
  if (catalog) trackItems("view_item_list", visible);
}

async function renderCollection(main, collection) {
  const params = new URLSearchParams(window.location.search);
  if (collection) params.set("collection", collection); else params.delete("collection");
  let catalog;
  try { catalog = await getProducts(params.toString()); } catch (error) {
    if (error.status === 404) return renderNotFound(main, "Колекцію не знайдено", "/collections/all");
    catalog = null;
  }
  if (collection && catalog && !catalog.collection) return renderNotFound(main);
  const title = catalog?.collection ? collectionText(catalog.collection, "name", document.documentElement.lang) : "Усі букети";
  setPageMeta(title + " у Львові | Квіткова Оаза", "Букети з цінами у Львові. Доступність на обрану дату підтвердить флорист.");
  main.className = "page-main collection-main";
  main.innerHTML = collectionMarkup(catalog?.products || [], catalog?.collection || collection, catalog?.collectionTotal || 0, params, !catalog, catalog?.collections || [], document.documentElement.lang);
  if (catalog) trackItems("view_item_list", catalog.products);
}

async function renderProduct(main, id) {
  const response = await fetch("/api/products/" + encodeURIComponent(id));
  if (!response.ok) return renderNotFound(main, "Букет не знайдено", "/collections/all");
  const { product } = await response.json();
  setPageMeta(product.name + " - " + money(product.priceCents) + " | Квіткова Оаза", "Доступність на обрану дату підтвердить флорист.");
  trackItems("view_item", [product]);
  main.innerHTML = productMarkup(product, document.documentElement.lang);
}

async function renderFavorites(main) {
  setPageMeta("Обрані букети | Квіткова Оаза", "Збережені на цьому пристрої букети.", { noindex: true });
  const favorites = readFavorites();
  const catalog = favorites.length ? (await getProducts()).products : [];
  const products = catalog.filter((p) => favorites.includes(p.id));
  main.innerHTML = '<header class="page-heading"><h1>Обрані букети</h1><p>Збережено лише на цьому пристрої. Реєстрація не потрібна.</p></header><div class="product-grid favorites-grid">' +
    (products.length ? products.map(productTile).join("") : '<section class="empty-page"><p>В обраному ще немає букетів</p><a class="button button-primary" href="/collections/all">Обрати букет</a></section>') +
    '</div>' + (favorites.length > products.length ? '<p class="helper-copy">Деякі збережені букети зараз недоступні.</p>' : "");
}

async function renderCart(main) {
  setPageMeta("Кошик | Квіткова Оаза", "Перевірте вибрані букети перед оформленням заявки.", { noindex: true });
  const cart = readCart();
  if (!cart.length) {
    main.innerHTML = `<section class="empty-page"><p class="eyebrow">ВАШЕ ЗАМОВЛЕННЯ</p><h1>Ваш кошик порожній</h1><a class="button button-primary" href="/collections/all">Обрати букет</a></section>`;
    return;
  }
  const catalog = (await getProducts("sort=number")).products;
  const byId = new Map(catalog.map((item) => [item.id, item]));
  const lines = cart.map((line) => ({ line, product: byId.get(line.id) })).filter(({ product }) => product);
  const missing = cart.filter((line) => !byId.has(line.id));
  const subtotal = cart.reduce((sum, line) => sum + line.priceCents * line.quantity, 0);
  const stale = lines.some(({ line, product }) => line.priceCents !== product.priceCents || line.version !== product.version);
  trackItems("view_cart", lines.map(({ product }) => product), Object.fromEntries(lines.map(({ line }) => [line.id, line.quantity])));
  main.innerHTML = `<header class="page-heading"><p class="eyebrow">ВАШЕ ЗАМОВЛЕННЯ</p><h1>Кошик</h1></header><div class="cart-layout"><section class="cart-lines" aria-label="Товари у кошику">${lines.map(({ line, product }) => cartRow(line, product)).join("")}${missing.map(cartMissingRow).join("")}</section><aside class="order-summary"><h2>Підсумок</h2><p class="summary-row"><span>Сума за букети</span><strong>${money(subtotal)}</strong></p><p class="helper-copy">Доставка лише в межах Львова - 350 грн. Від 3500 грн за букети доставка безкоштовна. Дату й час отримання підтвердить флорист.</p>${stale ? `<p class="inline-alert" role="alert">Ціна або дані букета змінилися. Підтвердіть оновлення перед оформленням.</p>` : ""}<button class="button button-primary button-full" type="button" data-action="checkout" ${stale || missing.length ? "disabled" : ""}>Оформити замовлення</button></aside></div>`;
}

function cartRow(line, product) {
  const priceChanged = line.priceCents !== product.priceCents || line.version !== product.version;
  return `<article class="cart-line"><a href="/products/${encodeURIComponent(product.id)}">${productImage(product, "thumbnail")}</a><div class="cart-line-details"><a class="cart-item-name" href="/products/${encodeURIComponent(product.id)}">${escapeHtml(product.name)}</a><p class="cart-line-price">${money(line.priceCents)} за шт.</p>${priceChanged ? `<p class="inline-alert" role="alert">Нова ціна: ${money(product.priceCents)}.</p><button class="text-link" type="button" data-action="accept-price" data-id="${escapeHtml(product.id)}">Оновити ціну</button>` : ""}<label>Кількість <input class="cart-quantity" type="number" min="1" max="99" value="${line.quantity}" inputmode="numeric" data-quantity-for="${escapeHtml(product.id)}" /></label><button class="text-link" type="button" data-action="remove-item" data-id="${escapeHtml(product.id)}">Видалити</button></div><strong class="cart-line-total">${money(line.priceCents * line.quantity)}</strong></article>`;
}

function cartMissingRow(line) {
  return `<article class="cart-line cart-line-missing"><div class="product-media product-media-thumbnail media-failed" role="img" aria-label="Фото букета більше недоступне"></div><div class="cart-line-details"><strong>${escapeHtml(line.id)}</strong><p class="inline-alert">Цей букет зараз недоступний для нової заявки.</p><button class="text-link" type="button" data-action="remove-item" data-id="${escapeHtml(line.id)}">Видалити</button></div><strong>${money(line.priceCents * line.quantity)}</strong></article>`;
}

async function renderCheckout(main, preserve = {}) {
  setPageMeta("Оформлення замовлення | Квіткова Оаза", "Надішліть заявку. Оплата зараз не потрібна, флорист вам перетелефонує.", { noindex: true });
  const cart = readCart();
  if (!cart.length) {
    main.innerHTML = `<section class="empty-page"><h1>Кошик порожній</h1><p>Спершу оберіть букет у каталозі.</p><a class="button button-primary" href="/collections/all">Обрати букет</a></section>`;
    return;
  }
  const catalog = (await getProducts("sort=number")).products;
  const byId = new Map(catalog.map((product) => [product.id, product]));
  const lines = cart.map((line) => ({ line, product: byId.get(line.id) }));
  const missing = lines.some(({ product }) => !product);
  const changed = lines.some(({ line, product }) => product && (line.priceCents !== product.priceCents || line.version !== product.version));
  const subtotal = cart.reduce((sum, line) => sum + line.priceCents * line.quantity, 0);
  trackItems("begin_checkout", lines.filter((item) => item.product).map((item) => item.product), Object.fromEntries(lines.map(({ line }) => [line.id, line.quantity])));
  main.innerHTML = checkoutMarkup({ lines, subtotal, missing, changed, preserve });
  const form = document.querySelector("#checkout-form");
  const trap = document.createElement("label");
  trap.className = "honeypot-field";
  trap.setAttribute("aria-hidden", "true");
  trap.innerHTML = '<span>Не заповнюйте це поле</span><input name="website" type="text" tabindex="-1" autocomplete="off" />';
  form.prepend(trap);
  const checkoutRoot = document.querySelector("#app");
  const submitButton = form.querySelector('[type="submit"]');
  if (checkoutRoot?.dataset.turnstileMode === "enabled") {
    const tokenField = document.createElement("input");
    tokenField.type = "hidden";
    tokenField.name = "turnstileToken";
    form.append(tokenField);
    const widget = document.createElement("div");
    widget.className = "turnstile-widget";
    widget.setAttribute("aria-label", "Перевірка форми від автоматичних заявок");
    const errorSummary = form.querySelector(".form-error-summary");
    errorSummary.before(widget);
    submitButton.disabled = true;
    loadTurnstileApi().then(() => {
      form.dataset.turnstileWidgetId = window.turnstile.render(widget, {
        sitekey: checkoutRoot.dataset.turnstileSitekey,
        action: "order_submit",
        callback: (token) => { tokenField.value = token; submitButton.disabled = false; },
        "expired-callback": () => { tokenField.value = ""; submitButton.disabled = true; },
        "error-callback": () => { tokenField.value = ""; submitButton.disabled = true; }
      });
    }).catch(() => {
      errorSummary.hidden = false;
      errorSummary.textContent = "Перевірка безпеки тимчасово недоступна. Спробуйте пізніше або зателефонуйте флористу.";
    });
  } else if (checkoutRoot?.dataset.turnstileMode === "unavailable") {
    submitButton.disabled = true;
    const message = document.createElement("p");
    message.className = "inline-alert";
    message.textContent = "Перевірка безпеки ще не налаштована для цього середовища.";
    form.querySelector(".form-error-summary").before(message);
  }
  form.addEventListener("change", handleDeliveryChoice);
  form.addEventListener("input", clearIdempotencyOnEdit);
  form.addEventListener("submit", submitOrder);
}

function field(name, label, type, value, options = {}) {
  const required = options.required ? "required" : "";
  const optional = options.optional ? `<span class="optional-label">необов'язково</span>` : "";
  const common = `id="field-${name}" name="${name}" ${required} ${options.autocomplete ? `autocomplete="${options.autocomplete}"` : ""} ${options.maxlength ? `maxlength="${options.maxlength}"` : ""} ${options.min ? `min="${options.min}"` : ""} ${options.placeholder ? `placeholder="${escapeHtml(options.placeholder)}"` : ""} aria-describedby="error-${name}"`;
  const input = type === "textarea"
    ? `<textarea ${common} rows="3">${escapeHtml(value)}</textarea>`
    : `<input type="${type}" ${common} value="${escapeHtml(value)}" />`;
  return `<div class="field-group"><label class="field-label" for="field-${name}">${label} ${optional}</label>${input}<small class="field-error" id="error-${name}" data-error-for="${name}" aria-live="polite"></small></div>`;
}

function handleDeliveryChoice(event) {
  if (!["receivingMethod", "isGift"].includes(event.target.name)) return;
  const form = event.currentTarget;
  const delivery = form.elements.receivingMethod.value === "delivery";
  form.querySelector(".delivery-address-field").hidden = !delivery;
  form.elements.deliveryAddress.required = delivery;
  const cash = form.querySelector('[name="paymentMethod"][value="cash"]');
  cash.disabled = delivery;
  form.querySelector("[data-cash-payment]").hidden = delivery;
  if (delivery && cash.checked) form.elements.paymentMethod.value = "bank_transfer";
  const totals = form.querySelector("[data-checkout-totals]");
  if (totals) {
    const subtotal = Number(totals.dataset.subtotalCents);
    const fee = deliveryCostCents(subtotal, delivery ? "delivery" : "pickup");
    totals.querySelector("[data-delivery-row]").hidden = !delivery;
    totals.querySelector("[data-delivery-fee]").textContent = money(fee);
    totals.querySelector("[data-order-total]").textContent = money(subtotal + fee);
  }
  const gift = delivery && form.elements.isGift.checked;
  form.querySelector(".recipient-fields").hidden = !gift;
  form.elements.recipientName.required = gift;
  form.elements.recipientPhone.required = gift;
}

function clearIdempotencyOnEdit() {
  sessionStorage.removeItem("ko-checkout-idempotency-key");
}

async function submitOrder(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const data = new FormData(form);
  const customerName = String(data.get("customerName") || "").trim();
  const customerPhone = String(data.get("customerPhone") || "").trim();
  const requestedDate = String(data.get("requestedDate") || "");
  const receivingMethod = String(data.get("receivingMethod") || "");
  const deliveryAddress = String(data.get("deliveryAddress") || "").trim();
  const customerComment = String(data.get("customerComment") || "").trim();
  const isGift = receivingMethod === "delivery" && data.get("isGift") === "on";
  const recipientName = isGift ? String(data.get("recipientName") || "").trim() : null;
  const recipientPhone = isGift ? String(data.get("recipientPhone") || "").trim() : null;
  const paymentMethod = String(data.get("paymentMethod") || "bank_transfer");
  clearFieldErrors();
  let invalid = false;
  if (customerName.length < 2 || customerName.length > 100) invalid = markFieldError("customerName", "Ім'я має містити від 2 до 100 символів.") || invalid;
  if (!validPhone(customerPhone)) invalid = markFieldError("customerPhone", "Введіть коректний номер телефону.") || invalid;
  if (!requestedDate || requestedDate < kyivToday()) invalid = markFieldError("requestedDate", "Оберіть сьогоднішню або майбутню дату.") || invalid;
  if (!["pickup", "delivery"].includes(receivingMethod)) invalid = markFieldError("receivingMethod", "Оберіть спосіб отримання.") || invalid;
  if (receivingMethod === "delivery" && (!deliveryAddress || deliveryAddress.length > 500)) invalid = markFieldError("deliveryAddress", "Вкажіть адресу доставки до 500 символів.") || invalid;
  if (customerComment.length > 1000) invalid = markFieldError("customerComment", "Коментар не може перевищувати 1000 символів.") || invalid;
  if (isGift && (!recipientName || recipientName.length < 2 || recipientName.length > 100)) invalid = markFieldError("recipientName", "Вкажіть ім'я отримувача від 2 до 100 символів.") || invalid;
  if (isGift && !validPhone(recipientPhone)) invalid = markFieldError("recipientPhone", "Введіть коректний телефон отримувача.") || invalid;
  if (!["bank_transfer", "cash"].includes(paymentMethod) || (receivingMethod === "delivery" && paymentMethod === "cash")) invalid = markFieldError("paymentMethod", "Оберіть спосіб оплати.") || invalid;
  if (invalid) {
    document.querySelector(".form-error-summary").hidden = false;
    document.querySelector(".form-error-summary").textContent = "Перевірте поля, позначені нижче.";
    form.querySelector("[aria-invalid='true']")?.focus();
    return;
  }
  const cart = readCart();
  if (!cart.length) return navigatePublic("/cart");
  const key = sessionStorage.getItem("ko-checkout-idempotency-key") || crypto.randomUUID();
  sessionStorage.setItem("ko-checkout-idempotency-key", key);
  const button = form.querySelector('[type="submit"]');
  const previous = button.textContent;
  button.disabled = true;
  button.textContent = "Надсилаємо…";
  document.querySelector(".form-error-summary").hidden = true;
  try {
    const attribution = await collectOrderAttribution();
    const response = await fetch("/api/orders", {
      method: "POST",
      headers: { "content-type": "application/json", "idempotency-key": key },
      body: JSON.stringify({
        customerName, customerPhone, requestedDate, receivingMethod,
        deliveryAddress, customerComment, isGift, recipientName, recipientPhone, paymentMethod, items: cart,
        website: String(data.get("website") || ""), turnstileToken: String(data.get("turnstileToken") || ""),
        attribution
      })
    });
    const result = await response.json();
    if (!response.ok) {
      if (result.error?.field) markFieldError(result.error.field, result.error.message);
      const summary = document.querySelector(".form-error-summary");
      summary.hidden = false;
      summary.textContent = result.error?.message || "Не вдалося надіслати заявку. Перевірте дані й повторіть спробу.";
      if (response.status === 409 && result.error?.code === "CATALOG_CHANGED") {
        const itemById = new Map((result.error.products || []).map((item) => [item.id, item]));
        checkoutCatalogProposal = cart.map((line) => ({ ...line, latest: itemById.get(line.id) }));
        summary.innerHTML = `${escapeHtml(result.error.message)} <a href="/cart">Перевірити кошик</a>`;
      }
      const botCheckFailed = ["BOT_CHECK_FAILED", "BOT_CHECK_UNAVAILABLE", "BOT_CHECK_NOT_READY"].includes(result.error?.code);
      if (botCheckFailed && window.turnstile && form.dataset.turnstileWidgetId) {
        tokenFieldIfPresent(form).value = "";
        window.turnstile.reset(form.dataset.turnstileWidgetId);
        button.disabled = true;
      } else {
        button.disabled = false;
      }
      button.textContent = previous;
      return;
    }
    clearCart();
    sessionStorage.removeItem("ko-checkout-idempotency-key");
    navigatePublic("/order-received");
  } catch {
    const summary = document.querySelector(".form-error-summary");
    summary.hidden = false;
    summary.textContent = "З'єднання перервано. Натисніть повторити, дані форми не очищені.";
    button.disabled = false;
    button.textContent = "Повторити надсилання";
  }
}

function tokenFieldIfPresent(form) {
  return form.querySelector('[name="turnstileToken"]') || { value: "" };
}

function loadTurnstileApi() {
  if (window.turnstile) return Promise.resolve();
  if (window.__koTurnstilePromise) return window.__koTurnstilePromise;
  window.__koTurnstilePromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
  return window.__koTurnstilePromise;
}

async function collectOrderAttribution() {
  let stored = null;
  try { stored = JSON.parse(localStorage.getItem("ko-attribution-source") || "null"); }
  catch { localStorage.removeItem("ko-attribution-source"); }
  if (!stored?.capturedAt || Date.now() - Date.parse(stored.capturedAt) > 30 * 86400_000) stored = null;
  const granted = localStorage.getItem("ko-analytics-consent") === "granted";
  if (!granted) return { consentGranted: false };
  const config = window.__KVITKOVA_TRACKING__ || {};
  const [clientId, sessionId] = await Promise.all([
    getAnalyticsValue(config.ga4Id, "client_id"),
    getAnalyticsValue(config.ga4Id, "session_id")
  ]);
  return {
    consentGranted: true,
    capturedAt: stored?.capturedAt || new Date().toISOString(),
    utm_source: stored?.utm_source || null,
    utm_medium: stored?.utm_medium || null,
    utm_campaign: stored?.utm_campaign || null,
    utm_content: stored?.utm_content || null,
    utm_term: stored?.utm_term || null,
    gclid: stored?.gclid || null,
    gbraid: stored?.gbraid || null,
    wbraid: stored?.wbraid || null,
    fbclid: stored?.fbclid || null,
    clientId,
    sessionId
  };
}

function getAnalyticsValue(measurementId, field) {
  if (!measurementId || typeof window.gtag !== "function") return Promise.resolve(null);
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), 1200);
    window.gtag("get", measurementId, field, (value) => {
      window.clearTimeout(timeout);
      resolve(value == null ? null : String(value));
    });
  });
}

function validPhone(value) {
  const compact = value.trim().replace(/[\s().-]/g, "");
  return /^0\d{9}$/.test(compact) || /^\+?380\d{9}$/.test(compact) || /^\+[1-9]\d{7,14}$/.test(compact);
}

function clearFieldErrors() {
  document.querySelectorAll("[data-error-for]").forEach((target) => { target.textContent = ""; });
  document.querySelectorAll("[aria-invalid='true']").forEach((target) => target.removeAttribute("aria-invalid"));
}

function markFieldError(name, message) {
  const target = document.querySelector(`[data-error-for="${CSS.escape(name)}"]`);
  const input = document.querySelector(`[name="${CSS.escape(name)}"]`);
  if (target) target.textContent = message;
  if (input) input.setAttribute("aria-invalid", "true");
  return Boolean(input);
}

async function renderReceipt(main) {
  setPageMeta("Замовлення отримано | Квіткова Оаза", "Захищена інформація про вашу заявку.", { noindex: true });
  const response = await fetch("/api/order-receipt", { cache: "no-store" });
  if (!response.ok) {
    main.innerHTML = `<section class="empty-page"><p class="eyebrow">ЗАХИЩЕНА СТОРІНКА</p><h1>Заявку не знайдено</h1><p>Відкрийте цю сторінку після успішного надсилання форми.</p><a class="button button-primary" href="/collections/all">До каталогу</a></section>`;
    return;
  }
  const { order } = await response.json();
  renderConfirmedOrderStatus(main, order);
  const trackReceipt = () => trackOrderRequest(order.orderNumber);
  trackReceipt();
  window.addEventListener("ko-analytics-consent-changed", trackReceipt);
}

function renderConfirmedOrderStatus(main, order) {
  const statusLabels = { new: "Очікує підтвердження флористом", confirmed: "Підтверджено флористом", fulfilled: "Виконано", cancelled: "Скасовано" };
  const isTestPayment = order.payment?.environment === "test" && order.paymentStatus === "paid";
  const paymentLabels = { not_created: "Оплата ще не створювалася", creating: "Створюємо рахунок", pending: "Очікуємо оплату", paid: isTestPayment ? "Тестову оплату підтверджено (без списання)" : "Оплату отримано", failed: "Платіж не пройшов", expired: "Рахунок прострочено", unknown: "Перевіряємо оплату", review_required: "Перевіряємо оплату", partially_refunded: "Частину оплати повернено", refunded: "Оплату повернено" };
  if (order.paymentMode === "bank_transfer") paymentLabels.not_created = order.status === "new" ? "Очікуємо погодження умов" : "Оплату ще не перевірено";
  if (order.paymentMethod === "cash") Object.assign(paymentLabels, {
    not_created: order.status === "new" ? "Очікуємо погодження умов" : "Готівку ще не отримано",
    paid: "Готівку отримано",
    partially_refunded: "Частину готівки повернено",
    refunded: "Готівку повернено"
  });
  const items = order.items.map((item) => '<article class="summary-item">' + productImage({ image: item.image, name: item.name }, "thumbnail") + '<div><strong>' + escapeHtml(item.name) + '</strong><small>' + item.quantity + ' × ' + money(item.priceCents) + '</small></div><strong>' + money(item.priceCents * item.quantity) + '</strong></article>').join("");
  const isDelivery = order.receivingMethod === "delivery";
  const recipient = order.isGift ? '<h3>Отримувач подарунка</h3><p translate="no">' + escapeHtml(order.recipientName || "") + '</p><p translate="no">' + escapeHtml(order.recipientPhone || "") + '</p>' : "";
  const method = '<h3>Спосіб оплати</h3><p>' + (order.paymentMethod === "cash" ? "Готівка при отриманні" : "Переказ ФОП") + '</p>';
  const subtotal = '<p class="summary-row"><span>Сума за букети</span><strong>' + money(order.bouquetSubtotalCents) + '</strong></p>';
  const delivery = order.deliveryCostCents == null ? "" : '<p class="summary-row"><span>Погоджена доставка</span><strong>' + money(order.deliveryCostCents) + '</strong></p>';
  const total = order.finalTotalCents == null ? "" : '<p class="summary-row summary-total"><span>Кінцева сума</span><strong>' + money(order.finalTotalCents) + '</strong></p>';
  const paymentNotice = order.paymentMethod === "cash"
    ? cashReceiptNotice(order)
    : isTestPayment
    ? '<p class="inline-alert" role="status">Це тестове середовище WayForPay. Реальні кошти не отримано, замовлення не можна виконувати.</p>'
    : order.paymentStatus === "review_required" ? '<p class="inline-alert" role="alert">Оплату перевіряє флорист. Будь ласка, зв’яжіться з нами перед наступною дією.</p>'
      : order.paymentStatus === "pending" && order.paymentMode === "bank_transfer"
        ? '<p class="helper-copy">Флорист надішле реквізити після погодження умов. Переказ вручну звіряється з банківською випискою.</p>'
          + (order.bankReceivedCents > 0 ? '<p class="summary-row"><span>Отримано за даними магазину</span><strong>' + money(order.bankReceivedCents) + '</strong></p><p class="summary-row"><span>Залишок до сплати</span><strong>' + money(order.bankBalanceCents) + '</strong></p>' : "")
      : ["unknown"].includes(order.paymentStatus) ? '<p class="helper-copy">Перевіряємо платіж. Не сплачуйте повторно, доки флорист не підтвердить результат.</p>' : "";
  const paymentLink = order.paymentMode !== "bank_transfer" && order.paymentStatus === "pending" && order.payment?.invoiceUrl
    ? '<p class="helper-copy">Рахунок дійсний до ' + escapeHtml(order.payment.expiresAt || "") + '.</p><a class="button button-primary button-full" href="' + escapeHtml(order.payment.invoiceUrl) + '" rel="nofollow">Перейти до оплати</a>'
    : "";
  const heading = isTestPayment ? "Тестову оплату підтверджено" : order.paymentStatus === "paid" ? "Оплату отримано" : order.status === "new" ? "Замовлення отримано" : "Погоджені умови замовлення";
  const summary = order.status === "new"
    ? "Флорист вам перетелефонує, щоб підтвердити букет, дату та умови отримання. Оплата відбувається після підтвердження."
    : "Це актуальні умови, які флорист погодив із вами.";
  main.innerHTML = '<section class="receipt-page"><div class="receipt-heading"><p class="eyebrow">ЗАЯВКА ' + escapeHtml(order.orderNumber) + '</p><h1>' + heading + '</h1><p>' + summary + '</p><p class="status-pill status-' + escapeHtml(order.status) + '">' + escapeHtml(statusLabels[order.status] || "Стан заявки уточнюється") + '</p><p class="status-pill status-payment-' + escapeHtml(order.paymentStatus || "not_created") + '">' + escapeHtml(paymentLabels[order.paymentStatus] || "Стан оплати уточнюється") + '</p></div><div class="receipt-layout"><section class="receipt-items"><h2>' + (order.conditionsVersion ? "Погоджені букети" : "Ваші букети") + '</h2>' + items + '</section><aside class="order-summary"><h2>Отримання</h2><p>' + escapeHtml(order.requestedDate) + '</p><p>' + (isDelivery ? "Доставка Львовом" : "Самовивіз") + '</p>' + (isDelivery ? '<p>' + escapeHtml(order.deliveryAddress || "") + '</p>' : "") + recipient + method + subtotal + delivery + total + paymentNotice + paymentLink + '</aside></div><div class="receipt-actions"><a class="button button-primary" href="/collections/all">Повернутися до каталогу</a><a class="button button-secondary" href="tel:' + PHONE_TEL + '" data-track="click_phone">Подзвонити флористу</a></div></section>';
}

function cashReceiptNotice(order) {
  if (order.paymentStatus === "review_required") return '<p class="inline-alert" role="alert">Готівковий запис перевіряє флорист. Зв’яжіться з нами перед наступною дією.</p>';
  if (order.cashReceivedCents > 0) {
    const refunded = order.cashRefundedCents > 0 ? '<p class="summary-row"><span>Повернено готівкою</span><strong>' + money(order.cashRefundedCents) + '</strong></p>' : "";
    const balance = order.paymentStatus === "paid" ? "" : '<p class="summary-row"><span>Залишок до сплати</span><strong>' + money(order.cashBalanceCents) + '</strong></p>';
    return '<p class="summary-row"><span>Залишилося після повернення</span><strong>' + money(order.cashReceivedCents) + '</strong></p>' + refunded + balance;
  }
  if (order.paymentStatus === "refunded") return '<p class="helper-copy">Готівку повернено. Флорист зв’яжеться з вами щодо подальших дій.</p>';
  return order.status === "cancelled" ? "" : '<p class="helper-copy">Готівка сплачується при доставці або самовивозі після погодження з флористом. Вибір цього способу ще не означає, що оплату отримано.</p>';
}

function renderDelivery(main) {
  setPageMeta("Доставка й самовивіз | Квіткова Оаза", "Доставка лише в межах Львова - 350 грн. Від 3500 грн за букети доставка безкоштовна. Дату й час отримання підтвердить флорист.");
  main.innerHTML = `<section class="content-page"><p class="eyebrow">ОТРИМАННЯ ЗАМОВЛЕННЯ</p><h1>Доставка й оплата</h1><div class="content-columns"><section><h2>Самовивіз</h2><p>Магазин: ${ADDRESS}.</p><a href="${MAP_URL}" target="_blank" rel="noopener noreferrer" data-track="click_directions">Прокласти маршрут</a></section><section><h2>Доставка Львовом</h2><p>Доставка лише в межах Львова - 350 грн. Від 3500 грн за букети доставка безкоштовна. Дату й час отримання підтвердить флорист.</p><p>Після заявки флорист вам перетелефонує, щоб узгодити дату та умови отримання.</p><p>Доставляємо якнайшвидше, не пізніше кінця погодженого дня доставки. Дату й час підтвердить флорист.</p><h2 id="returns">Заміна й повернення коштів</h2><p>Якщо букет пошкоджений або доставлено не той букет, зверніться до флориста за телефоном +380935809952. Магазин запропонує заміну або повернення коштів.</p></section><section><h2>Спосіб оплати</h2><p>Переказ ФОП після підтвердження флористом або готівка лише при самовивозі.</p><p>Щодня, 9:00-20:00</p></section></div><a class="button button-primary" href="/collections/all">Обрати букет</a></section>`;
}

function renderContacts(main) {
  setPageMeta("Контакти | Квіткова Оаза", "Квіткова Оаза у Львові, вул. Дністерська, 1.");
  main.innerHTML = `<section class="content-page contacts-page"><div class="contacts-details"><p class="eyebrow">КВІТКОВА ОАЗА · ЛЬВІВ</p><h1>Контакти</h1><div class="contact-list"><p><span>Адреса</span><strong>${ADDRESS}</strong></p><p><span>Телефон</span><a href="tel:${PHONE_TEL}" data-track="click_phone">${PHONE_DISPLAY}</a></p><p><span>Години роботи</span><strong>Щодня, 9:00-20:00</strong></p><p><span>Instagram</span><a href="${INSTAGRAM_URL}" target="_blank" rel="noopener noreferrer" data-track="click_instagram">@kvitkova_oaza</a></p></div><div class="receipt-actions"><a class="button button-primary" href="${MAP_URL}" target="_blank" rel="noopener noreferrer" data-track="click_directions">Прокласти маршрут</a><a class="button button-secondary" href="/collections/all">Обрати букет</a></div></div><img class="contacts-photo" src="/images/shop-entrance-900.webp" srcset="/images/shop-entrance-450.webp 450w, /images/shop-entrance-900.webp 900w" sizes="(max-width: 767px) calc(100vw - 80px), 350px" width="900" height="1600" alt="Вхід до магазину Квіткова Оаза у Львові" decoding="async" /></section>`;
}

function renderNotFound(main, title = "Сторінку не знайдено", href = "/") {
  setPageMeta(`${title} | Квіткова Оаза`, "Перевірте адресу або поверніться до каталогу.", { noindex: true });
  main.innerHTML = `<section class="empty-page"><p class="eyebrow">404</p><h1>${escapeHtml(title)}</h1><a class="button button-primary" href="${href}">${href === "/" ? "На головну" : "До каталогу"}</a></section>`;
}

async function getProducts(query = "") {
  const response = await fetch(`/api/products${query ? `?${query}` : ""}`, { cache: "no-store" });
  if (!response.ok) { const error = new Error("CATALOG_LOAD_FAILED"); error.status = response.status; throw error; }
  const result = await response.json();
  return result;
}

function emptyCollectionState() {
  return `<div class="catalog-state"><p class="empty-state">Зараз у цій колекції немає букетів</p><a class="button button-secondary" href="tel:${PHONE_TEL}">Подзвонити флористу</a></div>`;
}

function catalogErrorState() {
  return `<div class="catalog-state" role="alert"><p class="empty-state">Не вдалося завантажити букети</p><button class="button button-secondary" type="button" data-action="retry-catalog">Спробувати ще раз</button></div>`;
}

function attachStorefrontEvents() {
  attachProductSelectionTracking(document);
  document.querySelectorAll("[data-action='retry-catalog']").forEach((button) => {
    button.addEventListener("click", () => window.location.reload());
  });
  attachShoppingUI(getProducts);
  document.querySelectorAll("[data-action='remove-item']").forEach((button) => {
    button.addEventListener("click", () => {
      const line = readCart().find((item) => item.id === button.dataset.id);
      const product = line ? { id: line.id, name: line.id, collection: /^(autumn|wedding)-\d{2}$/.test(line.id) ? line.id.split("-")[0] : "", number: line.id.slice(-2), priceCents: line.priceCents } : null;
      removeFromCart(button.dataset.id);
      updateCartCount();
      if (product) trackItems("remove_from_cart", [product], { [product.id]: line.quantity });
      window.location.reload();
    });
  });
  document.querySelectorAll("[data-action='accept-price']").forEach((button) => {
    button.addEventListener("click", async () => {
      const response = await fetch(`/api/products/${encodeURIComponent(button.dataset.id)}`, { cache: "no-store" });
      if (!response.ok) return window.location.reload();
      acceptCurrentPrice((await response.json()).product);
      window.location.reload();
    });
  });
  document.querySelectorAll("[data-quantity-for]").forEach((input) => {
    input.addEventListener("change", () => {
      setQuantity(input.dataset.quantityFor, Number(input.value));
      window.location.reload();
    });
  });
  document.querySelector("[data-action='checkout']")?.addEventListener("click", () => navigatePublic("/checkout"));
  document.querySelector("#catalog-filter")?.addEventListener("submit", applyFilters);
  document.querySelector("#catalog-collection")?.addEventListener("change", (event) => {
    const params = new URLSearchParams(window.location.search); params.delete("collection");
    navigatePublic(`/collections/${encodeURIComponent(event.target.value)}${params.size ? `?${params}` : ""}`);
  });
  const filterPanel = document.querySelector(".catalog-filter-panel");
  if (filterPanel) {
    const mobile = window.matchMedia("(max-width: 767px)");
    const setDisclosure = () => { filterPanel.open = !mobile.matches || filterPanel.dataset.active === "true"; };
    setDisclosure();
    mobile.addEventListener("change", setDisclosure);
    document.querySelector('.catalog-sort select')?.addEventListener("change", () => document.querySelector("#catalog-filter").requestSubmit());
  }
  document.querySelector("[data-action='reset-filters']")?.addEventListener("click", () => {
    const collection = window.location.pathname.match(/^\/collections\/([a-z0-9-]+)/)?.[1] || "all";
    navigatePublic(`/collections/${collection}`);
  });
  document.querySelectorAll("[data-track]").forEach((element) => {
    element.addEventListener("click", () => track(element.dataset.track, { link_text: element.textContent.trim().slice(0, 80) }));
  });
}

function applyFilters(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const collection = window.location.pathname.match(/^\/collections\/([a-z0-9-]+)/)?.[1] || "all";
  const params = new URLSearchParams();
  if (form.elements.q?.value) params.set("q", form.elements.q.value);
  const min = form.elements.min.value;
  const max = form.elements.max.value;
  const sort = form.elements.sort.value;
  if (min) params.set("min", min);
  if (max) params.set("max", max);
  if (sort !== "number") params.set("sort", sort);
  navigatePublic(`/collections/${collection}?${params.toString()}`);
}

window.addEventListener("ko-cart-changed", updateCartCount);
